import { useState, useEffect } from 'react';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { ChevronDown, X, Music, Check } from 'lucide-react';
import { cn } from '../lib/utils';
import { API_BASE_URL as API } from '../config/api';
import { safeJson } from '../utils/safeJson';



/**
 * GenreSelector - Standardized genre selection component
 * 
 * Props:
 * - primaryGenre: string - Currently selected primary genre ID
 * - subGenre: string - Currently selected subgenre ID (optional)
 * - moodTag: string - Currently selected mood tag (optional)
 * - regionTag: string - Currently selected region tag (optional)
 * - onPrimaryGenreChange: (genreId: string) => void
 * - onSubGenreChange: (genreId: string) => void
 * - onMoodTagChange: (moodId: string) => void
 * - onRegionTagChange: (regionId: string) => void
 * - required: boolean - Whether primary genre is required
 * - showMood: boolean - Show mood selector (default true)
 * - showRegion: boolean - Show region selector (default true)
 * - compact: boolean - Compact mode for smaller spaces
 */
export default function GenreSelector({
  primaryGenre = '',
  subGenre = '',
  moodTag = '',
  regionTag = '',
  onPrimaryGenreChange,
  onSubGenreChange,
  onMoodTagChange,
  onRegionTagChange,
  required = true,
  showMood = true,
  showRegion = true,
  compact = false
}) {
  const [genres, setGenres] = useState([]);
  const [moods, setMoods] = useState([]);
  const [regions, setRegions] = useState([]);
  const [subGenres, setSubGenres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newSubGenreName, setNewSubGenreName] = useState('');
  const [creatingSubGenre, setCreatingSubGenre] = useState(false);
  const [expanded, setExpanded] = useState({
    genre: !primaryGenre,
    mood: false,
    region: false
  });

  useEffect(() => {
    fetchGenreTaxonomy();
  }, []);

  useEffect(() => {
    if (primaryGenre) {
      fetchSubGenres(primaryGenre);
    } else {
      setSubGenres([]);
    }
  }, [primaryGenre]);

  const fetchGenreTaxonomy = async () => {
    try {
      const res = await fetch(`${API}/api/reference/genre-list`);
      if (res.ok) {
        const data = await res.json();
        setGenres(data.genres || []);
        setMoods(data.moods || []);
        setRegions(data.regions || []);
      }
    } catch (err) {
      console.error('Failed to load genres:', err);
    } finally {
      setLoading(false);
    }
  };

  // Group genres by category
  const genresByCategory = genres.reduce((acc, genre) => {
    if (!acc[genre.category]) {
      acc[genre.category] = {
        name: genre.category_name,
        genres: []
      };
    }
    acc[genre.category].genres.push(genre);
    return acc;
  }, {});

  const fetchSubGenres = async (genreId) => {
    try {
      const res = await fetch(`${API}/api/reference/sub-genres?parent_genre_id=${genreId}`);
      if (res.ok) {
        const data = await res.json();
        setSubGenres(data.sub_genres || []);
      }
    } catch {}
  };

  const handleCreateSubGenre = async () => {
    if (!newSubGenreName.trim() || !primaryGenre) return;
    setCreatingSubGenre(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/reference/sub-genres`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ name: newSubGenreName.trim(), parent_genre_id: primaryGenre }),
      });
      if (res.ok) {
        const data = await res.json();
        setNewSubGenreName('');
        fetchSubGenres(primaryGenre);
        if (data.sub_genre) {
          onSubGenreChange?.(data.sub_genre.sub_genre_id);
        }
      }
    } catch {} finally {
      setCreatingSubGenre(false);
    }
  };

  const selectedGenre = genres.find(g => g.id === primaryGenre);
  const selectedMood = moods.find(m => m.id === moodTag);
  const selectedRegion = regions.find(r => r.id === regionTag);

  if (loading) {
    return (
      <div className="animate-pulse space-y-3">
        <div className="h-10 bg-muted/30 rounded-lg"></div>
        <div className="h-10 bg-muted/30 rounded-lg"></div>
      </div>
    );
  }

  return (
    <div className={cn("space-y-4", compact && "space-y-3")}>
      {/* Primary Genre - Required */}
      <div>
        <Label className="flex items-center gap-2 mb-2">
          <Music className="w-4 h-4 text-secondary" />
          Primary Genre {required && <span className="text-red-400">*</span>}
        </Label>
        
        {selectedGenre && !expanded.genre ? (
          <div 
            onClick={() => setExpanded(prev => ({ ...prev, genre: true }))}
            className="flex items-center gap-3 p-3 rounded-lg bg-secondary/10 border border-secondary/30 cursor-pointer hover:bg-secondary/20 transition-colors"
          >
            <span className="text-xl">{selectedGenre.emoji}</span>
            <div className="flex-1">
              <p className="font-medium">{selectedGenre.name}</p>
              <p className="text-xs text-muted-foreground">{selectedGenre.category_name}</p>
            </div>
            <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); onPrimaryGenreChange?.(''); }}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {selectedGenre && (
              <Button 
                variant="outline" 
                size="sm" 
                className="w-full justify-between"
                onClick={() => setExpanded(prev => ({ ...prev, genre: false }))}
              >
                <span className="flex items-center gap-2">
                  <span>{selectedGenre.emoji}</span>
                  {selectedGenre.name}
                </span>
                <ChevronDown className="w-4 h-4" />
              </Button>
            )}
            
            <div className="max-h-64 overflow-y-auto space-y-4 p-2 rounded-lg border border-border bg-muted/30">
              {Object.entries(genresByCategory).map(([categoryId, category]) => (
                <div key={categoryId}>
                  <p className="text-xs font-medium text-muted-foreground mb-2 sticky top-0 bg-background py-1">
                    {category.name}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {category.genres.map((genre) => (
                      <button
                        key={genre.id}
                        type="button"
                        onClick={() => {
                          onPrimaryGenreChange?.(genre.id);
                          setExpanded(prev => ({ ...prev, genre: false }));
                        }}
                        className={cn(
                          "px-3 py-1.5 rounded-full text-sm flex items-center gap-1.5 transition-all",
                          primaryGenre === genre.id
                            ? "bg-secondary text-white"
                            : "bg-muted/30 hover:bg-muted/50 border border-border"
                        )}
                      >
                        <span>{genre.emoji}</span>
                        <span>{genre.name}</span>
                        {primaryGenre === genre.id && <Check className="w-3 h-3" />}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Sub-Genre - Optional */}
      {primaryGenre && (
        <div>
          <Label className="flex items-center gap-2 mb-2">
            # Sub-Genre <span className="text-muted-foreground text-xs">(optional)</span>
          </Label>
          <div className="flex flex-wrap gap-2 mb-2">
            {subGenres.map((sg) => (
              <button
                key={sg.sub_genre_id}
                type="button"
                onClick={() => onSubGenreChange?.(subGenre === sg.sub_genre_id ? '' : sg.sub_genre_id)}
                className={cn(
                  "px-3 py-1.5 rounded-full text-sm flex items-center gap-1.5 transition-all",
                  subGenre === sg.sub_genre_id
                    ? "bg-secondary/30 text-secondary border border-secondary"
                    : "bg-muted/30 hover:bg-muted/50 border border-border"
                )}
              >
                # {sg.name}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setNewSubGenreName(newSubGenreName ? '' : ' ')}
              className="px-3 py-1.5 rounded-full text-sm flex items-center gap-1 bg-muted/30 hover:bg-muted/50 border border-dashed border-border text-muted-foreground"
            >
              + New
            </button>
          </div>
          {newSubGenreName !== '' && (
            <div className="flex gap-2 mt-2">
              <input
                type="text"
                value={newSubGenreName.trim()}
                onChange={(e) => setNewSubGenreName(e.target.value)}
                placeholder="Sub-genre name..."
                maxLength={50}
                className="flex-1 rounded-md border border-input bg-background px-3 py-1.5 text-sm"
                data-testid="new-subgenre-input"
              />
              <Button
                type="button"
                size="sm"
                onClick={handleCreateSubGenre}
                disabled={!newSubGenreName.trim() || creatingSubGenre}
                className="bg-secondary hover:bg-secondary/90"
              >
                Add
              </Button>
              <Button type="button" size="sm" variant="ghost" onClick={() => setNewSubGenreName('')}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Mood Tag - Optional */}
      {showMood && (
        <div>
          <Label className="flex items-center gap-2 mb-2">
            ✨ Mood / Vibe <span className="text-muted-foreground text-xs">(optional)</span>
          </Label>
          
          <div className="flex flex-wrap gap-2">
            {moods.map((mood) => (
              <button
                key={mood.id}
                type="button"
                onClick={() => onMoodTagChange?.(moodTag === mood.id ? '' : mood.id)}
                className={cn(
                  "px-3 py-1.5 rounded-full text-sm flex items-center gap-1.5 transition-all",
                  moodTag === mood.id
                    ? "bg-purple-500/30 text-purple-300 border border-purple-500"
                    : "bg-muted/30 hover:bg-muted/50 border border-border"
                )}
              >
                <span>{mood.emoji}</span>
                <span>{mood.name}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Region Tag - Optional */}
      {showRegion && (
        <div>
          <Label className="flex items-center gap-2 mb-2">
            📍 Region <span className="text-muted-foreground text-xs">(optional)</span>
          </Label>
          
          <div className="flex flex-wrap gap-2">
            {regions.map((region) => (
              <button
                key={region.id}
                type="button"
                onClick={() => onRegionTagChange?.(regionTag === region.id ? '' : region.id)}
                className={cn(
                  "px-3 py-1.5 rounded-full text-sm flex items-center gap-1.5 transition-all",
                  regionTag === region.id
                    ? "bg-blue-500/30 text-blue-300 border border-blue-500"
                    : "bg-muted/30 hover:bg-muted/50 border border-border"
                )}
              >
                <span>{region.emoji}</span>
                <span>{region.name}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Display selected tags summary */}
      {(primaryGenre || moodTag || regionTag) && (
        <div className="flex flex-wrap gap-2 pt-2 border-t border-border">
          {selectedGenre && (
            <Badge variant="outline" className="bg-secondary/20">
              {selectedGenre.emoji} {selectedGenre.name}
            </Badge>
          )}
          {selectedMood && (
            <Badge variant="outline" className="bg-purple-500/20">
              {selectedMood.emoji} {selectedMood.name}
            </Badge>
          )}
          {selectedRegion && (
            <Badge variant="outline" className="bg-blue-500/20">
              {selectedRegion.emoji} {selectedRegion.name}
            </Badge>
          )}
        </div>
      )}
    </div>
  );
}

// Simple dropdown version for compact spaces
export function GenreDropdown({ value, onChange, required = false, placeholder = "Select genre..." }) {
  const [genres, setGenres] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchGenres = async () => {
      try {
        const res = await fetch(`${API}/api/reference/genre-list`);
        if (res.ok) {
          const data = await res.json();
          setGenres(data.genres || []);
        }
      } catch (err) {
        console.error('Failed to load genres:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchGenres();
  }, []);

  // Group by category
  const genresByCategory = genres.reduce((acc, genre) => {
    if (!acc[genre.category_name]) acc[genre.category_name] = [];
    acc[genre.category_name].push(genre);
    return acc;
  }, {});

  return (
    <select
      value={value || ''}
      onChange={(e) => onChange?.(e.target.value)}
      className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
      required={required}
      disabled={loading}
    >
      <option value="">{loading ? 'Loading...' : placeholder}</option>
      {Object.entries(genresByCategory).map(([category, categoryGenres]) => (
        <optgroup key={category} label={category}>
          {categoryGenres.map((genre) => (
            <option key={genre.id} value={genre.id}>
              {genre.emoji} {genre.name}
            </option>
          ))}
        </optgroup>
      ))}
    </select>
  );
}
